import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../axios/axiosConfig';

// Async thunk to fetch documents from SharePoint via Graph API
export const fetchSharePointDocuments = createAsyncThunk(
  'sharePoint/fetchDocuments',
  async ({ 
    siteId, 
    driveId, 
    folderId = 'root',
    accessToken 
  }, { rejectWithValue }) => {
    try {
      if (!accessToken) {
        throw new Error('Access token is required');
      }

      // Build the Graph API URL - fixed to root children with top=50
      let graphUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${folderId}/children?$top=50`;

      const response = await axiosInstance.get(graphUrl, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      return {
        documents: response.data.value || [],
        totalCount: response.data['@odata.count'] || 0,
        folderId,
      };
    } catch (error) {
      return rejectWithValue({
        error: error.message || 'Failed to fetch SharePoint documents',
        status: error.response?.status,
      });
    }
  }
);

// Async thunk to fetch folder contents
export const fetchSharePointFolder = createAsyncThunk(
  'sharePoint/fetchFolder',
  async ({ 
    siteId, 
    driveId, 
    folderId, 
    accessToken 
  }, { rejectWithValue }) => {
    try {
      if (!accessToken) {
        throw new Error('Access token is required');
      }

      const graphUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${folderId}`;

      const response = await axiosInstance.get(graphUrl, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue({
        error: error.message || 'Failed to fetch folder',
        status: error.response?.status,
      });
    }
  }
);

const initialState = {
  documents: [],
  loading: false,
  error: null,
  totalCount: 0,
  currentFolderId: 'root',
  breadcrumb: [{ name: 'Root', folderId: 'root' }],
  folderDetails: null,
};

const sharePointSlice = createSlice({
  name: 'sharePoint',
  initialState,
  reducers: {
    resetSharePointState: () => initialState,
    setBreadcrumb: (state, action) => {
      state.breadcrumb = action.payload;
    },
    updateCurrentFolder: (state, action) => {
      state.currentFolderId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSharePointDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSharePointDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = action.payload.documents;
        state.totalCount = action.payload.totalCount;
        state.currentFolderId = action.payload.folderId;
      })
      .addCase(fetchSharePointDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Unknown error occurred';
      })
      .addCase(fetchSharePointFolder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSharePointFolder.fulfilled, (state, action) => {
        state.loading = false;
        state.folderDetails = action.payload;
      })
      .addCase(fetchSharePointFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Failed to fetch folder details';
      });
  },
});

export const {
  resetSharePointState,
  setBreadcrumb,
  updateCurrentFolder,
} = sharePointSlice.actions;

const selectSharePointState = (state) => state.sharePoint || initialState;

export const selectSharePointDocuments = (state) => selectSharePointState(state).documents;
export const selectSharePointLoading = (state) => selectSharePointState(state).loading;
export const selectSharePointError = (state) => selectSharePointState(state).error;
export const selectSharePointBreadcrumb = (state) => selectSharePointState(state).breadcrumb;
export const selectCurrentFolderId = (state) => selectSharePointState(state).currentFolderId;

export default sharePointSlice.reducer;